import React from "react";
import PropTypes from "prop-types";
import { _ } from "../utils/i18n";
import ContactForm from "./contact-form";
import styled from "styled-components";
import Button from "./button";
import sal from "sal.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faSpinner } from "@fortawesome/free-solid-svg-icons";
import Image from "../components/image.js";
import Heading from "./heading";

const LoadingIcon = styled(FontAwesomeIcon)`
  font-size: 1rem;
  color: var(--color-black);
  opacity: 0.8;
  position: absolute;
  right: 15%;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    right: 35%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    right: 23%;
  }
`;

const RadioButtons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: ${({ $direction }) => ($direction ? $direction : "row")};
  margin-top: 5rem;
  div {
    display: flex;
    flex-direction: row;
    align-items: center;
    @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
      flex-direction: column;
    }
  }
  & span {
    margin-bottom: 1rem;
  }
  & label {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    max-width: fit-content;
    margin-right: 2rem;
    @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
      margin-block: 1rem;
      max-width: 100%;
      margin-right: 0;
      align-items: flex-start;
    }
    span {
      margin-bottom: 0;
      @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
        width: 80%;
      }
    }
  }
`;
const Row = styled.div`
  display: flex;
  width: 100%;
  gap: 1rem;
  &.company-credentials {
    padding-bottom: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    flex-wrap: wrap;
    gap: 0;
  }
`;

const Required = styled.span`
  color: red;
`;

const SuccessWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6rem 1.5rem;
  font-size: 1.1rem;
  align-items: center;
  height: 100%;
  justify-content: center;

  @keyframes stroke {
    100% {
      stroke-dashoffset: 0;
    }
  }
  @keyframes scale {
    0%,
    100% {
      transform: none;
    }
    50% {
      transform: scale3d(1.1, 1.1, 1);
    }
  }
  @keyframes fill {
    100% {
      box-shadow: inset 0px 0px 0px 30px #7ac142;
    }
  }
`;

const InputContainer = styled.div`
  height: 50px;
  position: relative;
  width: 100%;
  input,
  textarea {
    position: relative;
    border-radius: 0.3rem;
    border: 1px solid var(--color-grey);
    outline: 0;
    width: 100%;
    margin-block: 1rem;
    font-size: 1rem;
    font-family: var(--font-paragraph);
    &:focus,
    :not(:placeholder-shown) {
      transform: translateY(0);
    }
    &:focus ~ label,
    :not(:placeholder-shown) ~ label {
      transform: translateY(-30px) translateX(10px) scale(0.75);
    }
    &:focus ~ label,
    :not(:placeholder-shown) ~ label {
      background-color: var(--color-white);
      padding: 0 1rem;
    }
  }
  input {
    height: 4rem;
    padding: 0.25rem 2.5rem 0;
  }
  textarea {
    height: 8rem;
    padding: 1rem 2.5rem;
    margin-block: 1rem;
  }
  label {
    color: var(--color-black);
    font-family: sans-serif;
    left: 1rem;
    line-height: 0.875rem;
    pointer-events: none;
    position: absolute;
    transform-origin: 0 50%;
    transition: transform 200ms, color 200ms;
    top: 2.5rem;
    font-family: var(--font-paragraph);
  }
`;
const FormProductInfo = styled.div`
  width: 100%;
  display: flex;
  margin-top: 2rem;
  align-items: center;
  gap: 1rem;
`;
const FormImageWrapper = styled.div`
  width: 20%;
`;
const FormContentWrapper = styled.div`
  width: 80%;
`;
const RequestWrapper = styled.div`
  width: 70%;
  margin: 2rem auto;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: 85%;
  }
`;
const GreyArticleNo = styled.p`
  color: var(--color-accent2);
  margin: 0;
`;
const StyledImage = styled(Image)`
  max-width: 100px;
`;
const IconWrapper = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: var(--color-body);
  color: var(--color-primary);
  display: flex;
  justify-content: center;
  align-items: center;
`;
const SuccessText = styled.p`
  text-align: center;
  margin-block: 0 3rem;
`;

const Cf7SendRequest = ({ id, action, html, product }) => {
  const getSubmission = () => {
    return {
      firstname: "",
      lastname: "",
      phone: "",
      email: "",
      message: "",
      validate: false,
      productName: product ? product?.name : "",
    };
  };

  const [formSubmission, setFormSubmission] = React.useState(getSubmission);
  const [successMessage, setSuccessMessage] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const formRef = React.useRef();
  const updateFormField = (form, name, value) => {
    const fields = form.querySelectorAll(`[name="${name}"]`);
    fields.forEach((field) => {
      if (field.type === "checkbox") {
        field.checked = value;
      } else {
        field.value = value;
      }
    });
  };
  const handleSubmit = (e, formSubmission) => {
    e.preventDefault();
    if (formRef.current) {
      const form = formRef.current.querySelector("form");
      if (form) {
        // Fill out the real form
        form.querySelectorAll("input, textarea");
        updateFormField(
          form,
          "send-request-firstname",
          formSubmission.firstname
        );
        updateFormField(form, "send-request-lastname", formSubmission.lastname);
        updateFormField(form, "send-request-phone", formSubmission.phone);
        updateFormField(form, "send-request-email", formSubmission.email);
        updateFormField(form, "send-request-message", formSubmission.message);
        updateFormField(
          form,
          "send-request-acceptance",
          formSubmission.validate
        );
        updateFormField(form, "produktnavn", formSubmission.productName);

        form.dispatchEvent(
          new CustomEvent("submitForm", {
            bubbles: true,
            cancelable: true,
            detail: { formTitle: "request" },
          })
        );
        setIsLoading(true);
      }
    }
  };
  const updateFormSubmission = (e) => {
    setFormSubmission({
      ...formSubmission,
      [e.target.name]:
        e.target.name === "validate" ? e.target.checked : e.target.value,
    });
  };
  const handleSuccess = (response) => {
    setSuccessMessage(response.message);
  };
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [successMessage]);

  React.useEffect(() => {
    const animations = sal();
    animations.disable();
  }, []);

  return (
    <>
      {successMessage ? (
        <SuccessWrapper>
          <IconWrapper>
            <FontAwesomeIcon icon={faEnvelope} size="3x" />
          </IconWrapper>
          <Heading
            content={_("Forespørsel sendt")}
            textAlign="center"
            margin="2rem 0"
          />

          <SuccessText>{successMessage}</SuccessText>
          <Button variant="filled" label="Fortsett å handle" url="/" />
        </SuccessWrapper>
      ) : (
        <RequestWrapper disableAnimation maxWidth="md">
          <Heading content={_("Forespørsel")} textAlign="center" />
          <FormProductInfo>
            <FormImageWrapper>
              <StyledImage image={product?.image} />
            </FormImageWrapper>

            <FormContentWrapper>
              <h3>{product?.name}</h3>
              {product?.sku && <GreyArticleNo>{product.sku}</GreyArticleNo>}
              <div
                dangerouslySetInnerHTML={{
                  __html: product?.shortDescription,
                }}
              />
            </FormContentWrapper>
          </FormProductInfo>

          <form
            onSubmit={(e) => {
              handleSubmit(e, formSubmission), setIsLoading(true);
            }}
          >
            <Row>
              <InputContainer className="ic1">
                <input
                  type="text"
                  value={formSubmission.firstname}
                  name="firstname"
                  id="fname"
                  required
                  onChange={updateFormSubmission}
                  placeholder=" "
                />
                <label htmlFor="firstname">
                  {_("Firmanavn")}
                  <Required>*</Required>
                </label>
              </InputContainer>
              <InputContainer className="ic1">
                <input
                  type="text"
                  value={formSubmission.lastname}
                  name="lastname"
                  required
                  onChange={updateFormSubmission}
                  id="lname"
                  placeholder=" "
                />
                <label htmlFor="lastname">
                  {_("Kontaktperson")}
                  <Required>*</Required>
                </label>
              </InputContainer>
            </Row>
            <Row>
              <InputContainer className="ic2">
                <input
                  type="tel"
                  value={formSubmission.phone}
                  name="phone"
                  pattern="[+]?[0-9 -]+"
                  onChange={updateFormSubmission}
                  id="tel"
                  placeholder=" "
                />
                <label htmlFor="phone">{_("Telefonnummer")}</label>
              </InputContainer>
              <InputContainer className="ic2">
                <input
                  type="email"
                  value={formSubmission.email}
                  name="email"
                  required
                  onChange={updateFormSubmission}
                  id="email"
                  placeholder=" "
                />
                <label htmlFor="email">
                  {_("E-post")}
                  <Required>*</Required>
                </label>
              </InputContainer>
            </Row>
            <Row>
              <InputContainer className="ic2">
                <textarea
                  value={formSubmission.message}
                  name="message"
                  onChange={updateFormSubmission}
                  id="message"
                  placeholder=" "
                />
                <label htmlFor="message">{_("Melding")}</label>
              </InputContainer>
            </Row>
            <RadioButtons>
              <label>
                <input
                  type="checkbox"
                  name="validate"
                  required
                  onChange={updateFormSubmission}
                />
                <span>
                  {_("Jeg godtar Epoxygrossisten AS sine retningslinjer for")}{" "}
                  <a href="/personvern" target="_blank" rel="noreferrer">
                    {_("personvern")}
                  </a>
                  <Required>*</Required>
                </span>
              </label>
            </RadioButtons>
            <div
              style={{
                display: "flex",
                alignContent: "flex-start",
                width: "100%",
                marginTop: "1rem",
              }}
            >
              <Button
                type="submit"
                textColor="var(--color-white)"
                style={{ marginTop: "1rem", position: "relative" }}
                disabled={isLoading}
              >
                {_("Send forespørsel")}
                {isLoading && (
                  <LoadingIcon icon={faSpinner} className="fa-spin" />
                )}
              </Button>
            </div>
          </form>
        </RequestWrapper>
      )}

      <ContactForm
        isHidden={true}
        ref={formRef}
        action={action}
        html={html}
        id={id}
        onSuccess={(response) => handleSuccess(response)}
      />
    </>
  );
};

Cf7SendRequest.propTypes = {
  action: PropTypes.string,
  id: PropTypes.number,
  html: PropTypes.string,
  product: PropTypes.shape({
    sku: PropTypes.string,
    name: PropTypes.string,
    shortDescription: PropTypes.string,
    image: PropTypes.object,
  }),
};

export default Cf7SendRequest;
